import { useEffect, useState } from 'react';
import type { HealthStatus } from '@mvcp/contracts';
import { fetchHealth } from '../api';

type HealthState =
  | { kind: 'loading' }
  | { kind: 'ok'; data: HealthStatus }
  | { kind: 'error'; message: string };

export function useHealth(): HealthState {
  const [state, setState] = useState<HealthState>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    fetchHealth(controller.signal)
      .then((data) => setState({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Unknown error',
        });
      });
    return () => controller.abort();
  }, []);

  return state;
}

export function HealthCard(): React.JSX.Element {
  const state = useHealth();
  const tone = state.kind === 'ok' ? 'ok' : state.kind === 'error' ? 'bad' : 'neutral';
  const label =
    state.kind === 'ok'
      ? 'API healthy'
      : state.kind === 'error'
        ? 'API unreachable'
        : 'Checking…';

  return (
    <section className="card stack" aria-labelledby="health-heading">
      <h2 id="health-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
        Platform status
      </h2>
      <span className="status-pill" data-tone={tone} role="status">
        <span className="status-pill__dot" aria-hidden="true" />
        {label}
      </span>

      {state.kind === 'ok' && (
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: 0 }}>
          <dt className="muted">Service</dt>
          <dd style={{ margin: 0 }} data-numeric>
            {state.data.service}
          </dd>
          <dt className="muted">Version</dt>
          <dd style={{ margin: 0 }} data-numeric>
            {state.data.version}
          </dd>
          <dt className="muted">Uptime</dt>
          <dd style={{ margin: 0 }} data-numeric>
            {state.data.uptimeSeconds}s
          </dd>
        </dl>
      )}

      {state.kind === 'error' && (
        <p className="muted" style={{ margin: 0 }}>
          {state.message}
        </p>
      )}
    </section>
  );
}
